import React, { useEffect, useRef, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { ApolloProvider, ServerError } from "@apollo/client";
import { Button, LocalizationProvider, MultiSelectField } from "@shoptet/ui";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Link, Route, Routes, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { WIZARD_STEP_PATH } from "./constants/paths";
import {
  ContextPreview,
  ContextWithBackground,
  PlaPreview,
  PlaWithBackground,
  SearchPreview,
  SearchPreviewMobile,
  SearchWithBackground,
  YoutubePreview,
  YoutubeWithBackground,
} from "./elements/AdPreview";
import { CampaignStopQuestionnaire } from "./elements/CampaignStopQuestionnaire";
import { DevThemeSwitcher } from "./elements/DevThemeSwitcher";
import { ErrorBoundary } from "./elements/ErrorBoundary";
import { Icon, IconKinds } from "./elements/Icons";
import Loading from "./elements/Loading/Loading";
import { ModalWrapper } from "./elements/ModalWrapper";
import ScrollToTop from "./elements/ScrollToTop";
import {
  AdSystemT,
  FeatureFlagT,
  MetaAdsWizardStepT,
  OrganizationNotReadyStepsT,
  ProductsCountStateT,
  WizardStepT,
  useOrganizationCampaignStateQuery,
  useOrganizationQuery,
  useStopCampaignQuestionnaireStatusQuery,
} from "./graphql/generated/graphql";
import useDefaultLanguage from "./hooks/useDefaultLanguage";
import { useFeatureFlag } from "./hooks/useFeatureFlag";
import { useWizardState } from "./hooks/useWizardState";
import { Error404Page } from "./pages/404Page";
import { Dashboard } from "./pages/Dashboard";
import { History } from "./pages/History";
import { HistoryAuditLogs } from "./pages/HistoryAuditLogs";
import { NoProducts } from "./pages/NoProducts";
import { NotReady, NotReadyApiWrapper } from "./pages/NotReady";
import { ProductsPage } from "./pages/Products";
import { Settings } from "./pages/Settings";
import { Check } from "./pages/wizard/Check";
import { Finish } from "./pages/wizard/Finish";
import { FirstPage } from "./pages/wizard/FirstPage";
import { ImageSelection } from "./pages/wizard/ImageSelection";
import { MetaAdtext } from "./pages/wizard/MetaAdtext";
import { MetaBudget } from "./pages/wizard/MetaBudget";
import { MetaFacebookPage } from "./pages/wizard/MetaFacebookPage";
import { MetaPreview } from "./pages/wizard/MetaPreview";
import { MetaProducts } from "./pages/wizard/MetaProducts";
import { NewCampaignSelection } from "./pages/wizard/NewCampaignSelection";
import { SuggestionGenerator } from "./pages/wizard/SuggestionGenerator";
import { TextSelection } from "./pages/wizard/TextSelection";
import { useAppSettings } from "./providers/appSettingsProvider";
import { createClient } from "./providers/graphqlProvider";
import { getUiLocale } from "./utils/getUiLocale";
import { isInIframe } from "./utils/isInIframe";
import { setClarityTag } from "./utils/setClarityTag";

export type AuthorizationGuardPropsT = {
  currentUrl: string;
};

const IndexPage = ({ path }: { path?: string }) => {
  const { t } = useTranslation("translation", { keyPrefix: "Index Page" });
  const navigate = useNavigate();
  return (
    <div>
      <h1>{t("Headline")}</h1>
      <div style={{ width: "390px", marginInline: "auto" }}>
        <div style={{ textAlign: "center" }}>
          <Icon className="icon-size-96" kind="delete-item" style={{ color: "var(--app-critical)" }} />
        </div>
        {path && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              data-test-id="continue-with-less-products"
              variant="primary"
              onClick={() => navigate(path, { replace: true })}
            >
              {t("Button")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const HomePage = () => {
  const { apiToken } = useAppSettings();
  const { data } = useOrganizationQuery({});
  const [counter, setCounter] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showModalLong, setShowModalLong] = useState(false);

  if (counter === 2) {
    throw new Error("Counter error - test");
  }

  return (
    <div>
      <h1>Hello Shoptet {data && data?.organization?.shoptetUrl}</h1>
      <Link to="/wizard">Start Wizard</Link>
      <br />
      <br />
      <Link to="/dashboard">Dashboard</Link>
      <br />
      <br />
      <Link to="/dashboard/products">Products</Link>
      <br />
      <br />
      <a href={`/admin?api_token=${apiToken}`}>Admin - Dashboard</a>
      <br />
      <br />
      <a href={`/admin/organizations?api_token=${apiToken}`}>Admin - Organization</a>
      <br />
      <br />
      <a href="/admin/sidekiq">SIDEKIQ</a>
      <br />
      <br />
      <ul>
        <li>
          <Link to="/wizard/new-campaign-selection">Select new campaign type</Link>
        </li>
        <li>
          <Link to="/wizard/">Google Step 1 - start</Link>
        </li>
        <li>
          <Link to="/wizard/suggestion-generator">Google Step 2 - suggestions</Link>
        </li>
        <li>
          <Link to="/wizard/image-selection">Google Step 3 - baner selection</Link>
        </li>
        <li>
          <Link to="/wizard/text-selection">Google Step 4 - text selection</Link>
        </li>
        <li>
          <Link to="/wizard/check">Google Step 5 - check</Link>
        </li>
        <li>
          <Link to="/wizard/finish">Google Step 6 - Finish</Link>
        </li>
        <li>
          <Link to="/wizard/meta-preview">Meta Step 1 - Start</Link>
        </li>
        <li>
          <Link to="/wizard/meta-page-linking">Meta Step 2 - FB page</Link>
        </li>
        <li>
          <Link to="/wizard/meta-products">Meta Step 3 - Excluding products</Link>
        </li>
        <li>
          <Link to="/wizard/meta-adtext">Meta Step 4 - Ad text</Link>
        </li>
        <li>
          <Link to="/wizard/meta-budget">Meta Step 5 - Budget</Link>
        </li>
        <li>
          <Link to="/wizard/meta-finish">Meta Step 6 - Finish</Link>
        </li>
      </ul>
      <br />
      <br />
      <Link to="/development/multiselect-component">Test Multiselect component</Link>
      {" | "}
      <Link to="/development/preview-ads">Showcase preview Ads</Link>
      {" | "}
      <Link to="/development/not-ready">Not ready test page</Link>
      {" | "}
      <Link to="/development/no-products">No products test page</Link>
      {" | "}
      <Link to="/development/icons">Icons list</Link>
      {" | "}
      <Link to="/development/admin-theme">Admin theme handshake</Link>
      {" | "}
      <Button onClick={() => setShowModal(true)}>Show modal</Button>
      {" | "}
      <Button onClick={() => setShowModalLong(true)}>Show long modal</Button>
      {" | "}
      <Button data-test-id="test-error-boundary-button" onClick={() => setCounter((current: number) => current + 1)}>
        Increment counter + 1 (if {counter} == 2 it throws error)
      </Button>
      {" | "}
      <a href="https://shoptet-stout.vercel.app/" target="_blank">
        Storybook of Shoptet UI components
      </a>
      {" | "}
      <a href="https://dotidot.myshoptet.com/admin/design-guide/" target="_blank">
        Shoptet admin design guide
      </a>
      <ModalWrapper isOpen={showModal} title="Some title" onClose={() => setShowModal(false)}>
        Foo Bar
      </ModalWrapper>
      <ModalWrapper isOpen={showModalLong} title="Some title" onClose={() => setShowModalLong(false)}>
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
        Foo Bar
        <br />
      </ModalWrapper>
    </div>
  );
};

const PREVIEW_DATA = {
  logo: "https://dotidot.myshoptet.com/user/logos/shoptet-logo.png",
  headline: "Suunto Core Alu Black with super long name",
  description: "Some super description which is super long",
  imageUrl: "https://dotidot.myshoptet.com/user/shop/detail/1-1_suunto-core-alu-black.jpg?65116ea6",
  price: "CZK 1,234.43",
  domain: "dotidot.myshoptet.com",
  businessName: "Dotidot",
};

type MultiSelectOptionT = {
  id: string;
  label: string;
};

const TestMultiselectComponent = () => {
  return (
    <MultiSelectField
      getOptionLabel={(option: MultiSelectOptionT) => option.label}
      getOptionValue={(option: MultiSelectOptionT) => option.id}
      label="Test Multiselect"
      options={[
        { id: "1", label: "Option 1" },
        { id: "2", label: "Option 2" },
        { id: "3", label: "Option 3" },
        { id: "4", label: "Option 4" },
        { id: "5", label: "Option 5" },
      ]}
    />
  );
};

const PreviewAds = () => {
  return (
    <div>
      <Link to="/">Home</Link>

      <h2>Context Ad preview</h2>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <ContextPreview {...PREVIEW_DATA} headline="Short headline" />
        <ContextPreview {...PREVIEW_DATA} description="Short description" headline="Short headline" />
        <ContextPreview {...PREVIEW_DATA} description="Short description" />
        <ContextPreview {...PREVIEW_DATA} />
      </div>

      <h2>Shopping Ad preview</h2>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <PlaPreview {...PREVIEW_DATA} />
        <PlaPreview {...PREVIEW_DATA} price="CZK 1,000,123" />
        <PlaPreview {...PREVIEW_DATA} headline="Suunto Core Alu Black with super long name and super moore content" />
      </div>

      <h2>Youtube Ad preview</h2>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <YoutubePreview {...PREVIEW_DATA} />
      </div>

      <h2>Search Ad preview Desktop</h2>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <SearchPreview
          {...PREVIEW_DATA}
          descriptions={["All available", "All in stock"]}
          headlines={["sdfsafas", "Best prices", "Sale"]}
        />
        <SearchPreview
          {...PREVIEW_DATA}
          descriptions={["All available", "All in stock"]}
          headlines={["sdfsafas super long", "Best prices and super", "And super longss"]}
        />
      </div>
      <h2>Search Ad preview Mobile</h2>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <SearchPreviewMobile
          {...PREVIEW_DATA}
          descriptions={["All available", "All in stock"]}
          headlines={["sdfsafas", "Best prices", "Sale"]}
        />
        <SearchPreviewMobile
          {...PREVIEW_DATA}
          descriptions={["All available", "All in stock"]}
          headlines={["sdfsafas super long", "Best prices and super", "And super longss"]}
        />
      </div>

      <br />
      <br />
      <h2>Context Ad preview With device</h2>
      <ContextWithBackground {...PREVIEW_DATA} />
      <br />
      <br />
      <h2>Shopping Ad preview With device</h2>
      <PlaWithBackground {...PREVIEW_DATA} />
      <br />
      <br />
      <h2>Search Ad preview With device</h2>
      <SearchWithBackground
        {...PREVIEW_DATA}
        descriptions={["popis", "All available"]}
        headlines={["Best prices", "Sale", "New items"]}
      />
      <br />
      <br />
      <h2>Youtube Ad preview With device</h2>
      <YoutubeWithBackground {...PREVIEW_DATA} />
    </div>
  );
};

const NotReadyTest = () => {
  return (
    <div>
      <Link to="/">Home</Link>

      <NotReady
        categoriesCount={50}
        loading={false}
        notReadySteps={[OrganizationNotReadyStepsT.DesignT, OrganizationNotReadyStepsT.ProductsT]}
        productFlagsCount={9}
        productsCount={300}
        productsCountToSynchronize={1500}
      />
      <NotReady
        categoriesCount={50}
        loading={false}
        notReadySteps={[OrganizationNotReadyStepsT.DesignT, OrganizationNotReadyStepsT.ProductsT]}
        productFlagsCount={9}
        productsCount={5500}
        productsCountToSynchronize={2000}
      />
      <NotReady
        categoriesCount={50}
        loading={false}
        notReadySteps={[OrganizationNotReadyStepsT.DesignT, OrganizationNotReadyStepsT.ProductsT]}
        productFlagsCount={9}
        productsCount={0}
        productsCountToSynchronize={0}
      />
    </div>
  );
};

const NoProductsTest = () => {
  return (
    <div>
      <Link to="/">Home</Link>

      <NoProducts />
    </div>
  );
};

const IconsExamples = () => {
  return (
    <div>
      <Link to="/">Home</Link>
      <ul>
        {IconKinds.map((icon) => (
          <li key={icon}>
            <Icon kind={icon} />
            <pre style={{ display: "inline-block", marginLeft: "20px" }}>&lt;Icon kind="{icon}" /&gt;</pre>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Stands in for the Shoptet admin so the theme handshake can be exercised without it: embeds the app
 * and emits `shoptet:admin-theme` on the iframe's load, on every toggle, and on request.
 */
const AdminThemePreview = () => {
  const { apiToken } = useAppSettings();
  const frame = useRef<HTMLIFrameElement>(null);
  const [dark, setDark] = useState(false);
  const [brand, setBrand] = useState(false);

  // dark and brand are independent switches in the admin, so all four combinations are reachable
  const emit = (isDark: boolean, isBrand: boolean) =>
    frame.current?.contentWindow?.postMessage(
      {
        type: "shoptet:admin-theme",
        bodyClasses: { "iframe-section": true, "theme-brand": isBrand, "theme-dark": isDark },
      },
      "*"
    );

  useEffect(() => {
    const answerRequest = (event: MessageEvent) => {
      if (event.data?.type === "shoptet:admin-theme-request" && event.source === frame.current?.contentWindow) {
        emit(dark, brand);
      }
    };
    window.addEventListener("message", answerRequest);
    return () => window.removeEventListener("message", answerRequest);
  }, [dark, brand]);

  return (
    <div>
      <Link to="/">Home</Link>
      <div style={{ margin: "16px 0", display: "flex", alignItems: "center", gap: "12px" }}>
        <Button
          onClick={() => {
            setDark(!dark);
            emit(!dark, brand);
          }}
        >
          {dark ? "Switch to light" : "Switch to dark"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setBrand(!brand);
            emit(dark, !brand);
          }}
        >
          {brand ? "Drop the brand theme" : "Add the brand theme"}
        </Button>
        <span>the embedded app below should follow, without reloading</span>
      </div>
      <iframe
        ref={frame}
        src={`/app?api_token=${apiToken}`}
        style={{ width: "100%", height: "80vh", border: "1px solid var(--app-border)" }}
        title="Embedded app"
        onLoad={() => emit(dark, brand)}
      />
    </div>
  );
};

const AuthorizationGuard = ({ currentUrl }: AuthorizationGuardPropsT) => {
  const navigate = useNavigate();
  const alreadyNavigated = useRef<boolean>(false);
  const {
    googleCurrentStep,
    googleWizardFinished,
    googleWizardInProgress,
    loading: wizardStateLoading,
    metaCurrentStep,
    metaWizardFinished,
    metaWizardInProgress,
  } = useWizardState();

  const { data, error, loading, startPolling, stopPolling } = useOrganizationCampaignStateQuery();
  const { data: stopCampaignQuestionnaireData } = useStopCampaignQuestionnaireStatusQuery();
  const { enabled: hasMetaCampaignsEnabled, loading: featureFlagLoading } = useFeatureFlag(FeatureFlagT.MetaCampaignsT);

  useEffect(() => {
    const notReadySteps = data?.organization?.notReadySteps;
    const step = googleCurrentStep;
    const metaStep = metaCurrentStep;

    if (Array.isArray(notReadySteps) && notReadySteps.length > 0) {
      startPolling(3000);
    } else {
      stopPolling();
      if ((step || metaStep) && !alreadyNavigated.current && !featureFlagLoading && !wizardStateLoading) {
        if (hasMetaCampaignsEnabled) {
          // Calculate wizard finished state directly from the data to avoid race conditions
          const isGoogleWizardFinished = step === WizardStepT.DashboardT;
          const isMetaWizardFinished = metaStep === MetaAdsWizardStepT.DashboardT;

          if (isGoogleWizardFinished || isMetaWizardFinished) {
            navigate("/dashboard", { replace: true });
          } else if (googleWizardInProgress) {
            navigate(WIZARD_STEP_PATH[step], { replace: true });
          } else if (metaWizardInProgress) {
            navigate(WIZARD_STEP_PATH[metaStep], { replace: true });
          } else {
            navigate("/wizard/new-campaign-selection", { replace: true });
          }
        } else {
          if (step) {
            navigate(WIZARD_STEP_PATH[step], { replace: true });
          }
        }
        alreadyNavigated.current = true;
      }
    }
  }, [
    data,
    featureFlagLoading,
    googleCurrentStep,
    googleWizardFinished,
    googleWizardInProgress,
    hasMetaCampaignsEnabled,
    metaCurrentStep,
    metaWizardFinished,
    metaWizardInProgress,
    navigate,
    startPolling,
    stopPolling,
    wizardStateLoading,
  ]);

  useEffect(() => {
    const projectId = data?.organization?.shoptetId;
    if (projectId) {
      setClarityTag("projectId", projectId);
    }
  }, [data]);

  if (error) {
    return <div>Something went wrong: {(error?.networkError as ServerError)?.statusCode}</div>;
  }
  if (loading) {
    return <Loading />;
  }

  if (data?.organization?.notReadySteps && data.organization.notReadySteps.length > 0) {
    return <NotReadyApiWrapper />;
  }

  if (data?.organization?.productsCountState === ProductsCountStateT.NoProductsT) {
    return <NoProducts />;
  }

  return (
    <div style={{ padding: isInIframe() ? 0 : "40px" }}>
      <ScrollToTop />
      <ErrorBoundary>
        {stopCampaignQuestionnaireData && (
          <CampaignStopQuestionnaire
            env={data?.env || ""}
            shoptetId={stopCampaignQuestionnaireData?.organization?.shoptetId || ""}
            source={stopCampaignQuestionnaireData?.organization?.properties?.stopCampaignQuestionnaireSource || ""}
            userEmail={stopCampaignQuestionnaireData?.user?.email || ""}
            stopCampaignQuestionnairePending={
              stopCampaignQuestionnaireData?.organization?.properties?.stopCampaignQuestionnairePending || false
            }
          />
        )}
        <Routes>
          <Route path={currentUrl}>
            <Route
              element={
                <IndexPage
                  path={
                    data?.organization?.googleAdsSetting?.wizardStep &&
                    WIZARD_STEP_PATH[data?.organization?.googleAdsSetting?.wizardStep]
                  }
                />
              }
              index
            />
            {data?.env !== "production" && <Route element={<HomePage />} path="/dev/homepage" />}
            <Route path="/wizard">
              <Route element={<NewCampaignSelection />} path="new-campaign-selection" />
              <Route element={<MetaPreview />} path="meta-preview" />
              <Route element={<MetaBudget />} path="meta-budget" />
              <Route element={<MetaFacebookPage />} path="meta-page-linking" />
              <Route element={<MetaProducts />} path="meta-products" />
              <Route element={<MetaAdtext />} path="meta-adtext" />
              <Route element={<FirstPage hasMetaCampaignsEnabled={hasMetaCampaignsEnabled || false} />} index />
              <Route
                element={<SuggestionGenerator hasMetaCampaignsEnabled={hasMetaCampaignsEnabled || false} />}
                path="suggestion-generator"
              />
              <Route
                element={<ImageSelection hasMetaCampaignsEnabled={hasMetaCampaignsEnabled || false} />}
                path="image-selection"
              />
              <Route
                element={<TextSelection hasMetaCampaignsEnabled={hasMetaCampaignsEnabled || false} />}
                path="text-selection"
              />
              <Route element={<Check hasMetaCampaignsEnabled={hasMetaCampaignsEnabled || false} />} path="check" />
              <Route element={<Finish hasMetaCampaignsEnabled={hasMetaCampaignsEnabled || false} />} path="finish" />
              <Route
                path="meta-finish"
                element={
                  <Finish adSystem={AdSystemT.MetaAdsT} hasMetaCampaignsEnabled={hasMetaCampaignsEnabled || false} />
                }
              />
            </Route>
            <Route path="dashboard">
              <Route element={<Dashboard />} index />
              <Route element={<ProductsPage />} path="products" />
            </Route>

            <Route element={<Settings />} path="settings" />

            <Route path="history">
              <Route element={<History />} index />
              <Route element={<HistoryAuditLogs />} path="audit-logs" />
            </Route>
            {data?.env !== "production" && (
              <>
                <Route element={<TestMultiselectComponent />} path="development/multiselect-component" />
                <Route element={<PreviewAds />} path="development/preview-ads" />
                <Route element={<NotReadyTest />} path="development/not-ready" />
                <Route element={<NoProductsTest />} path="development/no-products" />
                <Route element={<IconsExamples />} path="development/icons" />
                <Route element={<AdminThemePreview />} path="development/admin-theme" />
                <Route element={<Error404Page />} path="*" />
              </>
            )}
          </Route>
        </Routes>
        {data?.env !== "production" && (
          <div style={{ textAlign: "right", opacity: 0.5, marginTop: "10px" }}>
            <Link data-test-id="link-development-home" to="/dev/homepage">
              DEVELOPMENT HOME
            </Link>
          </div>
        )}
      </ErrorBoundary>
    </div>
  );
};

function App() {
  const { apiToken, apiUri, currentUrl, defaultLocale, urlLocale } = useAppSettings();

  useDefaultLanguage({ defaultLocale, urlLocale });

  return (
    <LocalizationProvider locale={getUiLocale(urlLocale || defaultLocale)}>
      <BrowserRouter basename="/app">
        <ApolloProvider client={createClient({ apiUri, apiToken })}>
          <AuthorizationGuard currentUrl={currentUrl} />
          <ToastContainer />
          <DevThemeSwitcher />
        </ApolloProvider>
      </BrowserRouter>
    </LocalizationProvider>
  );
}

export default App;
